import { useState, useEffect, useCallback } from 'react';
import {
  Bell, MessageSquare, Phone, FileText, AtSign,
  Users, Shield, X, Check, CheckCheck, Inbox,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useNotificationStore } from '../../stores';
import { fetchNotifications, markNotificationRead } from '../../lib/notifications';
import { Popover } from '../ui/Popover';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { tv, intlLocale } from '../../lib/i18nText';

type NotifTab = 'all' | 'unread' | 'mentions' | 'calls';

const TAB_CONFIG: { key: NotifTab; label: (t: TFunction) => string }[] = [
  { key: 'all',      label: (t) => t('layoutUi.notifications.tabs.all', 'All') },
  { key: 'unread',   label: (t) => t('layoutUi.notifications.tabs.unread', 'Unread') },
  { key: 'mentions', label: (t) => t('layoutUi.notifications.tabs.mentions', 'Mentions') },
  { key: 'calls',    label: (t) => t('layoutUi.notifications.tabs.calls', 'Calls') },
];

function getIcon(type: string) {
  switch (type) {
    case 'message':    return <MessageSquare className="h-4 w-4 text-frost-0" />;
    case 'mention':    return <AtSign className="h-4 w-4 text-aurora-2" />;
    case 'call':       return <Phone className="h-4 w-4 text-aurora-3" />;
    case 'file':       return <FileText className="h-4 w-4 text-aurora-4" />;
    case 'team':       return <Users className="h-4 w-4 text-frost-2" />;
    case 'security':   return <Shield className="h-4 w-4 text-nord-11" />;
    default:           return <Bell className="h-4 w-4 text-nord-4" />;
  }
}

function relativeTime(t: TFunction, language: string | undefined, iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('layoutUi.time.justNow', 'Just now');
  if (mins < 60) return tv(t, 'layoutUi.time.minutesAgo', '{{n}}m ago', { n: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return tv(t, 'layoutUi.time.hoursAgo', '{{n}}h ago', { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return tv(t, 'layoutUi.time.daysAgo', '{{n}}d ago', { n: days });
  return new Date(iso).toLocaleDateString(intlLocale(language));
}

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
  /** The bell button the panel opens from */
  anchorRef?: React.RefObject<HTMLElement | null>;
  onNavigate?: (type: string, targetId?: string, messageId?: string) => void;
  /** Opens the Today view, where everything that needs you is listed */
  onOpenToday?: () => void;
}

export function NotificationPanel({ open, onClose, anchorRef, onNavigate, onOpenToday }: NotificationPanelProps) {
  const { t, i18n } = useTranslation();
  const { notifications, setNotifications, markRead, markAllRead } = useNotificationStore();
  const [tab, setTab] = useState<NotifTab>('all');
  const [loading, setLoading] = useState(false);

  // Fetch notifications on open
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchNotifications()
      .then(items => { if (items) setNotifications(items); })
      .catch(() => { /* keep what the live socket delivered */ })
      .finally(() => setLoading(false));
  }, [open, setNotifications]);

  const handleMarkRead = useCallback((id: string) => {
    markRead(id);
    void markNotificationRead(id);
  }, [markRead]);

  const handleMarkAllRead = useCallback(() => {
    markAllRead();
    // Mark all on server
    notifications.filter(n => !n.read).forEach(n => { void markNotificationRead(n.id); });
  }, [markAllRead, notifications]);

  const handleClick = useCallback((notif: typeof notifications[0]) => {
    if (!notif.read) handleMarkRead(notif.id);
    onNavigate?.(notif.type, notif.targetId, notif.messageId);
  }, [handleMarkRead, onNavigate]);

  // Filter by tab
  const filtered = notifications.filter(n => {
    if (tab === 'unread') return !n.read;
    if (tab === 'mentions') return n.type === 'mention';
    if (tab === 'calls') return n.type === 'call';
    return true;
  });

  if (!open) return null;

  return (
    <Popover
      open
      anchorRef={anchorRef}
      onClose={onClose}
      className="w-96 overflow-hidden rounded-xl border border-nord-3/40 bg-nord-1 shadow-2xl animate-dropdown-in"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-nord-3 px-4 py-3">
        <h2 className="text-sm font-semibold text-nord-6">{t('topbar.notifications', 'Notifications')}</h2>
        <div className="flex items-center gap-1">
          <button
            onClick={handleMarkAllRead}
            className="rounded p-1 text-xs text-nord-4 hover:text-frost-0 transition-colors"
            title={t('layoutUi.notifications.markAllRead', 'Mark all as read')}
            aria-label={t('layoutUi.notifications.markAllRead', 'Mark all as read')}
          >
            <CheckCheck className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="rounded p-1 text-nord-4 hover:text-nord-6 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-nord-3/60 px-2">
        {TAB_CONFIG.map(cfg => (
          <button
            key={cfg.key}
            onClick={() => setTab(cfg.key)}
            className={clsx(
              'flex-1 py-2 text-xs font-medium transition-colors',
              tab === cfg.key
                ? 'border-b-2 border-frost-0 text-frost-0'
                : 'text-nord-4 hover:text-nord-6',
            )}
          >
            {cfg.label(t)}
            {cfg.key === 'unread' && notifications.filter(n => !n.read).length > 0 && (
              <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-nord-11 px-1 text-[10px] font-bold text-white">
                {notifications.filter(n => !n.read).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Notification List */}
      <div className="max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-frost-0 border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-nord-4">
            <Inbox className="mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm font-medium">
              {tab === 'unread' ? t('layoutUi.notifications.caughtUp', "You're all caught up!") : t('layoutUi.notifications.empty', 'No notifications')}
            </p>
            <p className="mt-1 text-xs opacity-60">
              {tab === 'unread' ? t('layoutUi.notifications.caughtUpHint', 'All notifications have been read') : t('layoutUi.notifications.emptyHint', 'Nothing to show here yet')}
            </p>
          </div>
        ) : (
          filtered.map(notif => (
            <button
              key={notif.id}
              onClick={() => handleClick(notif)}
              className={clsx(
                'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-nord-2/60',
                !notif.read && 'bg-frost-0/5',
              )}
            >
              {/* Icon */}
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-nord-2">
                {getIcon(notif.type)}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <p className={clsx('text-sm leading-snug', notif.read ? 'text-nord-4' : 'text-nord-6 font-medium')}>
                  {notif.title}
                </p>
                {notif.body && (
                  <p className="mt-0.5 truncate text-xs text-nord-4/80">{notif.body}</p>
                )}
                <p className="mt-1 text-[10px] text-nord-muted">{relativeTime(t, i18n?.language, notif.createdAt)}</p>
              </div>

              {/* Unread indicator */}
              {!notif.read && (
                <div className="mt-2 flex shrink-0 items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-frost-0" />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMarkRead(notif.id); }}
                    className="rounded p-0.5 text-nord-4 hover:text-frost-0"
                    title={t('layoutUi.notifications.markRead', 'Mark as read')}
                    aria-label={t('layoutUi.notifications.markRead', 'Mark as read')}
                  >
                    <Check className="h-3 w-3" />
                  </button>
                </div>
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-nord-3/60 px-4 py-2">
          <button
            onClick={() => { onClose(); onOpenToday?.(); }}
            className="w-full rounded-lg py-1.5 text-center text-xs text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors"
          >
            {t('layoutUi.notifications.viewAll', 'View all on Today')}
          </button>
        </div>
      )}
    </Popover>
  );
}

/* ── Notification Badge (for TopBar bell icon) ─────────────── */
export function NotificationBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  const display = count > 99 ? '99+' : String(count);
  return (
    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-nord-11 px-1 text-[10px] font-bold text-white">
      {display}
    </span>
  );
}
